import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";

import getSymbolFromCurrency from "currency-symbol-map";
import Swal from "sweetalert2";

import {
  Paper,
  Title,
  Text,
  Container,
  Group,
  Box,
  Divider,
  Image,
  createStyles,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import SpaceModel from "../../models/Space";
import StripeModel from "../../models/Stripe";

import useLangUser from "../../utils/LangUser";

import TiptapReadonly from "../tiptap/components/TiptapReadonly";
import TaxAndDiscount from "../spaces_neo/settings/payment/TaxAndDiscount";
import CouponButton from "../spaces_neo/settings/payment/CouponButton";
import FlexibleList from "../spaces_neo/settings/payment/FlexibleList";
import BundlesList from "../spaces_neo/settings/payment/BundlesList";

const useStyles = createStyles(() => ({
  spaceContainer: {
    ".ProseMirror p": {
      color: "#000000",
    },
    ".ProseMirror a": {
      color: "#000000",
    },
    ".ProseMirror .file-node svg": {
      stroke: "#000000",
    },
    ".ProseMirror .file-node a": {
      color: "#000000",
    },
  },
}));

export default function SpaceReference(props) {
  const { t } = useTranslation();
  const currentLocation = useLocation();
  const { classes } = useStyles();
  const { language } = useLangUser();
  
  const smallScreen = useMediaQuery("(max-width: 768px)");

  const [space, setSpace] = useState(null);
  const [discount_type, setDiscountType] = useState(null);
  const [discount_value, setDiscountValue] = useState(null);
  const [selectedFlexible, setSelectedFlexible] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [bundlesType, setBundlesType] = useState("regular");
  const [isShowOnlyBundles, setIsShowOnlyBundles] = useState(false);

  const space2 = useSelector(state => state.space);

  const price = useRef(null);
  const currency = useRef(null);
  const frequency = useRef(null);
  const coupon = useRef(null);

  const shouldShowPaymentBox = bundlesType == "regular" || props.isStartPage;
  const isMember = props.isMember;
  const tax_amount = space?.payment?.tax_collection ? Number(props.tax_amount || 0) : null;
  const discount = typeof discount_value == "number" ? discount_value : 0;

  useEffect(() => {
    fetchSpace();
  }, []);

  useEffect(() => {
    if (selectedFlexible) {
      price.current = selectedFlexible.otherPrice ? Number(selectedFlexible.otherPrice) : selectedFlexible.amount;
      frequency.current = selectedFlexible.frequency || "once";
      if (coupon.current) {
        onApplyCode(null, coupon.current);
      } else {
        onPriceChange(price.current, currency.current, frequency.current);
      }
    }
  }, [selectedFlexible])

  useEffect(() => {
    if (selectedBundle) {
      price.current = Number(selectedBundle.default_payment_amount);
      currency.current = selectedBundle.currency;
      frequency.current = selectedBundle.default_payment_frequency || "once";
      window.$selectedBundleId = selectedBundle.id;
      if (coupon.current) {
        onApplyCode(null, coupon.current);
      } else {
        onPriceChange(price.current, currency.current, frequency.current);
      }
    } else {
      window.$selectedBundleId = null;
    }
  }, [selectedBundle])

  const fetchSpace = () => {
    const locationBundleId = new URLSearchParams(currentLocation.search).get("bundles");
    const cookieBundleId = localStorage.getItem('bundles');
    const bundleID = locationBundleId || cookieBundleId;

    SpaceModel.onFetchSimple(
      props.slug,
      "payment",
      (data) => {
        setSpace(data);
        currency.current = data.payment.currency;
        
        const _bundle = Array.isArray(data.bundles) ? data.bundles.find((item) => {
          return item.id == Number(bundleID);
        }) : null;

        if (typeof props.setShowNewsLetter === "function"){
          props.setShowNewsLetter(data.show_newsletter);
        }
        if (typeof props.setShowSpaceReference === "function") {
          props.setShowSpaceReference(data.privacy_level === "private" ? false : true);
        }
        if (typeof props.setProviders === "function") {
          props.setProviders(data.sso_providers);
        }
        if (data.is_paid && typeof props.setStepper === "function") {
          props.setStepper(true);
        }

        const bundlesFiltered = Array.isArray(data.bundles) && data.bundles.filter((item) => item.show_in_checkout);

        if (!data.payment.price && bundlesFiltered.length > 0 && !data.payment.is_flexible && !data.payment.invitation_has_different_price) {
          setSelectedBundle(data.bundles[0]);
          setIsShowOnlyBundles(true);
        } else if (_bundle && !data.payment.invitation_has_different_price) {
          setSelectedBundle(_bundle);
          setBundlesType("bundle");
        } else if (data.payment.is_flexible) {
          setSelectedFlexible(data.payment.price[0]);
        } else {
          price.current = data.payment.price;
          frequency.current = data.payment.frequency || "once";
          
          onPriceChange(price.current, currency.current, frequency.current);
        }

        setTimeout(() => {
          if (currentLocation.pathname.indexOf("/pay") >= 0) {
            localStorage.removeItem('bundles');
          }
        }, 5000);

      },
      (error) => {
        if (typeof props.setSpaceReference === "function") {
          props.setSpaceReference(null);
        }
      }
    );
  };

  const onApplyCode = (event, couponCode) => {
    if (event) {
      event.stopPropagation();
    }
    if (couponCode === "") {
      return;
    }
    coupon.current = couponCode;
    StripeModel.onApplyCode(
      props.slug,
      couponCode,
      price.current,
      frequency.current,
      currency.current,
      (data) => {
        setDiscountType(data.data.type);
        setDiscountValue(data.data.value);
        onPriceChange(data.data.new_total, currency.current, frequency.current, data.data.coupon);
      },
      (e) => {
        if (e?.title) {
          Swal.fire(e.title, e.message, "error");
        }
      }
    );
  };

  const onPriceChange = (_price, _currency, _frequency, _coupon) => {
    if (typeof props.onPriceChange === "function") {
      props.onPriceChange(_price, _currency, _frequency, _coupon);
    }
  };

  const ensure2Dec = (value) => {
    value = value ? parseFloat(value) : 0;
    return value.toFixed(2).replace(".00", "");
  };

  const showCouponButton = () => {
    if (space.is_paid && !isMember && space?.has_coupons && !props.hideCoupon) {
      return <CouponButton onApplyCode={onApplyCode} />;
    } else {
      return <Box mb={15} />;
    }
  };

  const getPaymentDetails = () => {
    const _currency = getSymbolFromCurrency(space.payment.currency);
    const _amount = ensure2Dec(space.payment.price);
    const _frequency = space.payment.frequency && space.payment.frequency != "once" ? ` / ${space.payment.frequency.replace("per ", "")}` : "";
    if (space.payment.frequency == "once") {
      return `One time payment of ${_currency}${_amount}`;
    }
    if (space.payment.subscription_type == "ongoing") {
      if (space.payment.frequency == "per month") {
        return `Monthly subscription of ${_currency}${_amount}${_frequency}`;
      } else {
        return `Annual subscription of ${_currency}${_amount}${_frequency}`;
      }
    }
    if (space.payment.number_of_times && space.payment.number_of_times > 0) {
      return `${space.payment.number_of_times} ${
        space.payment.number_of_times == 1 ? "payment" : "payments"
      } of ${_currency}${_amount}`;
    }
    if (space.payment.until_date) {
      return `${_currency}${_amount} ${_frequency} until ${DateTime.fromISO(space.payment.until_date)
        .setLocale(language)
        .toFormat("MMM d, y")}`;
    }
    return `${_currency}${_amount}`;
  };

  if (!space) {
    return null;
  }

  const hasDescription = Array.isArray(space?.payment?.description) && space.payment.description.filter(item => !(item.type === "paragraph" && !('content' in item))).length > 0;
  const totalPrice = selectedFlexible ? (selectedFlexible.otherPrice ? Number(selectedFlexible.otherPrice) : selectedFlexible.amount) : space.payment.price;

  return (
    <Container
      size={500}
      mb={40}
      mt={smallScreen ? 10 : 40}
      className={classes.spaceContainer}
    >
      {shouldShowPaymentBox && (
        <Paper
          withBorder
          shadow="xs"
          p={0}
          mt={30}
          radius="md"
        >
          {!!space.header?.url && (
            <Image
              sx={(theme) => ({
                borderTopLeftRadius: theme.radius.md,
                borderTopRightRadius: theme.radius.md,
                overflow: "hidden",
              })}
              src={space.header.url}
              alt={space.name}
            />
          )}

          <Box pt={25} pb={8} pl={35} pr={35}>
            {space.top_parent && <Text dimmed>{space.top_parent}</Text>}
            <Title
              sx={(theme) => ({ fontSize: 24, fontWeight: 600 })}
              order={2}
              mb={20}
            >
              {space.name}
            </Title>
            {typeof space2?.calendar?.plain_description === "string" && space2.calendar.plain_description.trim().length > 0 && (
              <>
                <Divider
                  sx={(theme) => ({ borderTopColor: theme.colors.gray[2] })}
                  my="sm"
                />
                <Text pt={10}>{space2.calendar.plain_description.replace(/<br>/g, " ")}</Text>
              </>
            )}

            {space.is_paid &&
              (!space.payment.is_flexible ||
                space.payment.invitation_has_different_price) &&
              !isMember && (
                <Text mt={5} size="lg" color="dimmed">
                  {`${getPaymentDetails()}`}
                </Text>
              )}

            {space.is_paid && space.payment.is_flexible && !space.payment.invitation_has_different_price && !isMember && (
              <FlexibleList
                data={space.payment.price}
                formatted_price={space.payment.formatted_price}
                selectedFlexible={selectedFlexible}
                selectedBundle={selectedBundle}
                currency={getSymbolFromCurrency(space.payment.currency)}
                setSelectedFlexible={(value) => {
                  setSelectedFlexible(value);
                  setSelectedBundle(null);
                }}
              />
            )}

            {showCouponButton()}

            {space.is_paid && !isMember && !props.notLoggedIn && (
              <>
                <TaxAndDiscount
                  currency={getSymbolFromCurrency(space.payment.currency)}
                  tax_amount={tax_amount}
                  discount_type={discount_type}
                  discount_value={discount_value}
                />
                <Group position="apart" mb={15}>
                  {space.payment.has_trial && space.payment.trial_period > 0 && space.payment?.frequency !== "once" ? (
                    <Text size="lg">{`Total due in ${space.payment.trial_period} days`}</Text>
                  ) : (
                    <Text size="lg">{t("users.spaceReference.total")}</Text>
                  )}
                  <Text size="lg" sx={(theme) => ({ fontWeight: 600 })}>
                    {`${getSymbolFromCurrency(space.payment.currency)}
                      ${ensure2Dec(
                        Number(totalPrice) + tax_amount - (discount_type === "percent" ? (totalPrice * discount / 100) : discount)
                      )}
                    `}
                  </Text>
                </Group>

                {space.payment.is_flexible && hasDescription && (
                  <>
                    <Box pb={15}>
                      <Divider
                        sx={(theme) => ({ borderTopColor: theme.colors.gray[2] })}
                        my="sm"
                      />
                    </Box>
                    <TiptapReadonly content={space.payment.description} />
                    <Box mb={20}></Box>
                  </>
                )}
              </>
            )}

          </Box>
        </Paper>
      )}

      {!(isMember || space.payment.with_invitation) && !props.notLoggedIn && (
        <BundlesList
          data={space.bundles}
          bundlesType={bundlesType}
          shouldShowPaymentBox={shouldShowPaymentBox}
          selectedBundle={selectedBundle}
          tax_amount={tax_amount}
          discount_type={discount_type}
          discount_value={discount_value}
          setSelectedBundle={(value) => {
            coupon.current = null;
            setDiscountType(null);
            setDiscountValue(null);
            setSelectedBundle(value);
            if (!value) {
              fetchSpace();
            }
          }}
          onApplyCode={onApplyCode}
        />
      )}

    </Container>
  );
}
